import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronUp, MoveRight, XCircleIcon } from "lucide-react";
import Avatar from "../../../../../../../components/shared/Avatar";
import { Badge } from "../../../../../../../components/ui/badge";
import ShiftDetails from "./ShiftDetails";
import { useUser } from "../../../../../../../utils/hooks/user";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { getId, getShiftInfo } from "./helpers";
import { TYPE_CLASSNAMES } from "./constants";
import { cn } from "../../../../../../../lib/utils";

interface ShiftCardProps {
    request: any;
    claim: (request: any) => void;
    deny: (request: any) => void;
    focused?: boolean;
}

function ShiftCard({ request, claim, deny, focused = false }: ShiftCardProps) {
    const { t } = useTranslation();
    const user = useUser();
    const [open, setOpen] = useState(focused);

    // a notification linked to this request: bring it into view once it mounts
    const scrollIntoView = useCallback((element: HTMLDivElement | null) => {
        if (focused) {
            element?.scrollIntoView({ block: "center" });
        }
    }, [focused]);

    const { owner } = getShiftInfo(request);
    const isDirectOffer = request?.type === "direct-offer";
    const offerUser = request?.data?.offerUser;
    const isOfferedToMe = !!user?.id
        && (getId(offerUser) === user.id || (request?.requiredToBeApprovedBy || []).includes(user.id));
    const canClaim = !!user?.id
        && getId(owner) !== user.id
        && !request?.data?.takeUser
        && (!isDirectOffer || isOfferedToMe);

    const Chevron = open ? ChevronUp : ChevronDown;

    return (
        <div
            ref={scrollIntoView}
            className={cn(
                "tw-border tw-rounded-lg tw-p-3 tw-mb-2",
                focused && "tw-border-primary tw-ring-1 tw-ring-primary"
            )}
        >
            <div
                className={cn(
                    "tw-flex tw-items-center tw-justify-between",
                    open && "tw-border-b tw-pb-3 tw-mb-3"
                )}
            >
                <div className="tw-flex tw-items-center tw-text-sm tw-gap-2">
                    {owner && <Avatar user={owner} size="sm" />}
                    <span>{owner ? renderUserName(owner) : t("open-shift")}</span>
                    {isDirectOffer && offerUser && (
                        <>
                            <MoveRight size={14} className="tw-text-neutral-400" />
                            <Avatar user={offerUser} size="sm" />
                            <span className="tw-font-medium">
                                {isOfferedToMe ? t("you") : renderUserName(offerUser)}
                            </span>
                        </>
                    )}
                    {request?.type && (
                        <Badge variant="outline" className={cn("tw-whitespace-nowrap", TYPE_CLASSNAMES[request.type])}>
                            {t(request.type)}
                        </Badge>
                    )}
                </div>

                <div className="tw-flex tw-gap-3 tw-items-center">
                    {canClaim && (
                        <>
                            <XCircleIcon
                                className="tw-text-rose-500 tw-cursor-pointer"
                                onClick={() => deny(request)}
                            />
                            <CheckCircle2
                                className="tw-text-primary tw-cursor-pointer"
                                onClick={() => claim(request)}
                            />
                        </>
                    )}

                    <Chevron
                        className="tw-text-neutral-500 tw-cursor-pointer"
                        size={19}
                        onClick={() => setOpen((prev) => !prev)}
                    />
                </div>
            </div>

            {open && <ShiftDetails request={request} />}
        </div>
    );
}

export default ShiftCard;
